/**
 * PhotoCaptureStep — Step 3 of the visitor registration drawer.
 *
 * Displays a webcam preview, lets staff capture a photo of the visitor,
 * then uploads it to POST /visitors/:id/photo/.
 *
 * States:
 *   previewing → webcam stream live, showing video
 *   captured   → still image captured, showing preview with Retake / Confirm
 *   uploading  → POST in flight
 *
 * The step is optional — staff can Skip at any point, which closes
 * the drawer and leaves the visitor without a photo.
 *
 * Accessibility:
 *   - All buttons are keyboard-accessible
 *   - Status messages use aria-live
 *   - Video element has appropriate aria-label
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, RotateCcw, Check, X, Loader2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useWebcam } from '../hooks/useWebcam'
import { useUploadVisitorPhoto } from '../hooks'
import { cn } from '@/lib/utils'

interface PhotoCaptureStepProps {
  visitorId:    string
  visitorName:  string
  onComplete:   () => void   // photo uploaded or skipped
  onSkip:       () => void   // no photo, close drawer
}

type CaptureState = 'previewing' | 'captured' | 'uploading'

export default function PhotoCaptureStep({
  visitorId,
  visitorName,
  onComplete,
  onSkip,
}: PhotoCaptureStepProps) {
  const { videoRef, status, errorMessage, start, stop, capture } = useWebcam()
  const uploadMutation = useUploadVisitorPhoto(visitorId)

  const [captureState, setCaptureState] = useState<CaptureState>('previewing')
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)
  const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null)
  const previewImgRef = useRef<HTMLImageElement>(null)

  // Start webcam as soon as the step mounts
  useEffect(() => {
    void start()
    return () => stop()
  }, [start, stop])

  const handleCapture = useCallback(() => {
    const blob = capture()
    if (!blob) return
    setCapturedBlob(blob)
    const url = URL.createObjectURL(blob)
    setCapturedDataUrl(url)
    setCaptureState('captured')
  }, [capture])

  const handleRetake = useCallback(() => {
    if (capturedDataUrl) URL.revokeObjectURL(capturedDataUrl)
    setCapturedBlob(null)
    setCapturedDataUrl(null)
    setCaptureState('previewing')
  }, [capturedDataUrl])

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (capturedDataUrl) URL.revokeObjectURL(capturedDataUrl)
    }
  }, [capturedDataUrl])

  const handleConfirm = useCallback(() => {
    if (!capturedBlob) return
    setCaptureState('uploading')
    uploadMutation.mutate(capturedBlob, {
      onSuccess: () => {
        if (capturedDataUrl) URL.revokeObjectURL(capturedDataUrl)
        onComplete()
      },
      onError: () => {
        // Reset to captured state so staff can retry or skip
        setCaptureState('captured')
      },
    })
  }, [capturedBlob, capturedDataUrl, uploadMutation, onComplete])

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-sm font-semibold">Capture visitor photo</p>
        <p className="text-xs text-muted-foreground">
          Optional — take a photo of <strong>{visitorName}</strong> for identification.
        </p>
      </div>

      {/* Camera area */}
      <div className="relative overflow-hidden rounded-xl bg-black aspect-video">
        {/* Video preview — hidden once captured */}
        <video
          ref={videoRef}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity',
            captureState === 'previewing' ? 'opacity-100' : 'opacity-0',
          )}
          aria-label="Camera preview"
          playsInline
          muted
        />

        {/* Captured still image */}
        {capturedDataUrl && (
          <img
            ref={previewImgRef}
            src={capturedDataUrl}
            alt="Captured visitor photo"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* Loading / denied overlays */}
        {(status === 'requesting') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Requesting camera access…</p>
          </div>
        )}
        {(status === 'denied' || status === 'error') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white">
            <AlertTriangle className="h-8 w-8 text-amber-400" />
            <p className="text-sm">{errorMessage ?? 'Camera unavailable.'}</p>
            <Button size="sm" variant="outline" className="text-white border-white/40" onClick={() => void start()}>
              Try again
            </Button>
          </div>
        )}

        {/* Upload spinner overlay */}
        {captureState === 'uploading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
        )}

        {/* Live indicator */}
        {captureState === 'previewing' && status === 'streaming' && (
          <div
            className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 text-xs text-white"
            aria-live="polite"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
            LIVE
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between gap-3">
        {/* Left: Skip */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          onClick={onSkip}
          disabled={captureState === 'uploading'}
        >
          <X className="h-4 w-4" />
          Skip
        </Button>

        {/* Right: context-dependent primary actions */}
        <div className="flex items-center gap-2">
          {captureState === 'captured' && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleRetake}
              disabled={false}
            >
              <RotateCcw className="h-4 w-4" />
              Retake
            </Button>
          )}

          {captureState === 'previewing' ? (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={handleCapture}
              disabled={status !== 'streaming'}
            >
              <Camera className="h-4 w-4" />
              Capture
            </Button>
          ) : (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={handleConfirm}
              disabled={captureState === 'uploading'}
            >
              {captureState === 'uploading' ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Saving…</>
              ) : (
                <><Check className="h-4 w-4" />Use photo</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
